import { useEffect, useState } from "react";
import { Send, Diamond, CheckCircle2, XCircle, Crown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function RechargePage() {
  const { reseller, refresh } = useAuth();
  const [platform, setPlatform] = useState("trumeet");
  const [platforms, setPlatforms] = useState([
    { key: "trumeet", name: "Trumeet" },
    { key: "olaparty", name: "Qwick Live" },
  ]);
  const [opMode, setOpMode] = useState("transfer"); // "transfer" | "noble"
  const [recipient, setRecipient] = useState("");
  const [diamonds, setDiamonds] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [last, setLast] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [recipientInfo, setRecipientInfo] = useState(null);

  const [nobleTiers, setNobleTiers] = useState([]);
  const [nobleTier, setNobleTier] = useState("");
  const [nobleMode, setNobleMode] = useState("first"); // "first" | "renew"
  const [currentNoble, setCurrentNoble] = useState(null);

  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    api.get("/reseller/platforms").then(({ data }) => {
      if (!Array.isArray(data)) return;
      setPlatforms(data);
      if (data.length && !data.some((p) => p.key === platform)) setPlatform(data[0].key);
    }).catch(() => {});
    api.get("/reseller/noble/config").then(({ data }) => {
      const tiers = data?.tiers || [];
      setNobleTiers(tiers);
      if (tiers.length) setNobleTier(tiers[0].key);
    }).catch(() => {});
  }, []);

  const nobleAvailable = platform === "olaparty";
  useEffect(() => {
    if (!nobleAvailable && opMode === "noble") setOpMode("transfer");
  }, [nobleAvailable, opMode]);

  const balance = (reseller?.balances?.[platform]?.balance) ?? 0;
  const currentPlatform = platforms.find((p) => p.key === platform);
  const multiplier = currentPlatform?.multiplier ?? (platform === "olaparty" ? 200 : 1);
  const typedQty = parseInt(diamonds, 10) || 0;
  const receiveAmount = typedQty * multiplier;
  const selectedTier = nobleTiers.find((t) => t.key === nobleTier);
  const nobleCost = selectedTier ? (selectedTier[nobleMode]?.diamond ?? 0) : 0;
  const nobleCharge = multiplier > 1 ? Math.round(nobleCost / multiplier) : nobleCost;

  const verifyRecipient = async () => {
    if (!recipient) {
      toast.error("Enter a recipient ID first");
      return;
    }
    setVerifying(true);
    setRecipientInfo(null);
    setCurrentNoble(null);
    try {
      const { data } = await api.get(`/reseller/lookup/${encodeURIComponent(recipient.trim())}?platform=${platform}`);
      setRecipientInfo(data);
      toast.success(`Found: ${data.nick || data.uid}`);
      if (platform === "olaparty") {
        api.get(`/reseller/noble/detect/${encodeURIComponent(recipient.trim())}`).then(({ data: nd }) => {
          if (!nd.ok) return;
          const cur = nd.current || {};
          setCurrentNoble(cur);
          if (cur.has_noble && cur.tier_key) {
            setNobleTier(cur.tier_key);
            setNobleMode("renew");
          }
        }).catch(() => {});
      }
    } catch (err) {
      setRecipientInfo(null);
      toast.error(formatApiError(err.response?.data?.detail) || "Recipient not found");
    } finally {
      setVerifying(false);
    }
  };

  const submitTransfer = async () => {
    const n = parseInt(diamonds, 10);
    if (!recipient || !n || n <= 0) {
      toast.error("Enter recipient ID and a positive diamond amount");
      return;
    }
    if (n > balance) {
      toast.error(`Insufficient balance (${balance})`);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/reseller/recharge", {
        recipient_uid: recipient, diamonds: n, note, platform,
      });
      setLast(data);
      if (data.status === "success") {
        toast.success(`Recharged ${n} diamonds to ${data.recipient?.nick || recipient}`);
        setRecipient(""); setDiamonds(""); setNote(""); setRecipientInfo(null);
      } else {
        toast.error("Recharge failed — diamonds refunded");
      }
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitNoble = async () => {
    if (!recipient) { toast.error("Enter recipient ID"); return; }
    if (!nobleTier) { toast.error("Select a noble tier"); return; }
    if (nobleCost <= 0) { toast.error("This tier's price is not configured. Contact admin."); return; }
    if (nobleCharge > balance) { toast.error(`Insufficient balance (${balance.toLocaleString()})`); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/reseller/noble", {
        recipient_uid: recipient, tier: nobleTier, mode: nobleMode, note, platform,
      });
      setLast(data);
      if (data.status === "success") {
        toast.success(`${data.tier_label} ${nobleMode === "renew" ? "renewed" : "activated"} for ${data.recipient?.nick || recipient}`);
        setRecipient(""); setNote(""); setRecipientInfo(null);
      } else {
        toast.error("Noble recharge failed — diamonds refunded");
      }
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    setLast(null);
    if (opMode === "noble") submitNoble();
    else submitTransfer();
  };

  return (
    <div className="space-y-6" data-testid="recharge-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Operation</p>
        <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">New Recharge</h1>
        <p className="text-zinc-500 mt-1">Transfer diamonds or activate a noble title for a recipient.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-lg p-6">
          <form onSubmit={submit} className="space-y-4" data-testid="recharge-form">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Platform</Label>
              <div className="flex flex-wrap gap-2 mt-1" data-testid="platform-selector">
                {platforms.map((p) => (
                  <button
                    type="button"
                    key={p.key}
                    onClick={() => { setPlatform(p.key); setRecipientInfo(null); }}
                    className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      platform === p.key
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
                    }`}
                    data-testid={`platform-option-${p.key}`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            </div>

            {nobleAvailable && (
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Operation type</Label>
                <div className="flex gap-2 mt-1" data-testid="op-mode-selector">
                  <button
                    type="button"
                    onClick={() => setOpMode("transfer")}
                    className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors inline-flex items-center justify-center gap-2 ${
                      opMode === "transfer" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
                    }`}
                    data-testid="op-mode-transfer"
                  >
                    <Diamond className="w-4 h-4" /> Transfer Diamonds
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpMode("noble")}
                    className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors inline-flex items-center justify-center gap-2 ${
                      opMode === "noble" ? "bg-amber-500 text-white border-amber-500" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
                    }`}
                    data-testid="op-mode-noble"
                  >
                    <Crown className="w-4 h-4" /> Noble Recharge
                  </button>
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Recipient ID</Label>
              <div className="flex gap-2">
                <Input
                  value={recipient}
                  onChange={(e) => { setRecipient(e.target.value); setRecipientInfo(null); }}
                  placeholder="e.g. 2095978736"
                  required
                  data-testid="recharge-uid-input"
                />
                <Button type="button" variant="outline" onClick={verifyRecipient} disabled={verifying || !recipient} data-testid="verify-recipient-btn">
                  {verifying ? "…" : "Verify"}
                </Button>
              </div>
              <p className="text-xs text-zinc-500 mt-1">The in-app ID shown on the recipient's profile.</p>
              {recipientInfo && (
                <div className="mt-3 flex items-center gap-3 bg-zinc-50 border border-zinc-200 rounded-lg p-3" data-testid="recipient-info">
                  {recipientInfo.avatar ? (
                    <img src={recipientInfo.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-zinc-200" />
                  )}
                  <div className="min-w-0">
                    <div className="font-medium text-zinc-900 truncate" data-testid="recipient-nick">{recipientInfo.nick || "Unknown"}</div>
                    <div className="text-xs text-zinc-500 font-mono">ID: {recipientInfo.vid}</div>
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-green-600 ml-auto flex-shrink-0" />
                </div>
              )}
            </div>

            {opMode === "transfer" ? (
            <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Diamond amount</Label>
                <Input
                  type="number" min="1" value={diamonds}
                  onChange={(e) => setDiamonds(e.target.value)}
                  placeholder="100" required={opMode === "transfer"}
                  data-testid="recharge-amount-input"
                />
                <p className="text-xs text-zinc-500 mt-1">Available: <span className="font-mono">{balance.toLocaleString()}</span></p>
                {multiplier > 1 && typedQty > 0 && (
                  <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center justify-between" data-testid="transfer-preview-box">
                    <div className="text-sm text-emerald-900">
                      Recipient receives <span className="text-[11px] text-emerald-700">({typedQty.toLocaleString()} × {multiplier})</span>
                    </div>
                    <div className="font-display text-xl font-semibold text-emerald-900 tabular-nums font-mono" data-testid="transfer-preview-value">
                      {receiveAmount.toLocaleString()} 💎
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                {currentNoble && currentNoble.has_noble && (
                  <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-2.5 flex items-center gap-2 text-sm" data-testid="reseller-noble-current">
                    <Crown className="w-4 h-4 text-amber-600" />
                    <span className="text-amber-900">Recipient holds <strong>{currentNoble.tier_label || 'a noble title'}</strong>{currentNoble.expire_at ? ` (expires ${new Date(currentNoble.expire_at * 1000).toLocaleDateString()})` : ''} — <strong>Renewal</strong> pre-selected.</span>
                  </div>
                )}
                {currentNoble && currentNoble.has_noble === false && (
                  <div className="mb-3 rounded-lg border border-zinc-200 bg-zinc-50 p-2.5 text-sm text-zinc-500" data-testid="reseller-noble-none">
                    No active noble title on this account — pick a tier to activate.
                  </div>
                )}
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Noble tier</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1" data-testid="noble-tier-selector">
                    {nobleTiers.map((t) => (
                      <button
                        type="button"
                        key={t.key}
                        onClick={() => setNobleTier(t.key)}
                        className={`px-3 py-3 rounded-lg border text-sm font-medium transition-colors flex flex-col items-center gap-1 ${
                          nobleTier === t.key ? "bg-amber-500 text-white border-amber-500" : "bg-white text-zinc-700 border-zinc-300 hover:border-amber-400"
                        }`}
                        data-testid={`noble-tier-${t.key}`}
                      >
                        <Crown className="w-4 h-4" />
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Type</Label>
                  <div className="flex gap-2 mt-1" data-testid="noble-mode-selector">
                    <button
                      type="button"
                      onClick={() => setNobleMode("first")}
                      className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        nobleMode === "first" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
                      }`}
                      data-testid="noble-mode-first"
                    >
                      First-time
                    </button>
                    <button
                      type="button"
                      onClick={() => setNobleMode("renew")}
                      className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        nobleMode === "renew" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"
                      }`}
                      data-testid="noble-mode-renew"
                    >
                      Renewal
                    </button>
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between" data-testid="noble-cost-box">
                  <div className="text-sm text-amber-900">
                    {selectedTier?.label || "—"} · {nobleMode === "renew" ? "Renewal" : "First-time"}
                    {multiplier > 1 && nobleCost > 0 && (
                      <span className="block text-[11px] text-amber-700">Recipient value {nobleCost.toLocaleString()} 💎 ÷ {multiplier}</span>
                    )}
                  </div>
                  <div className="font-display text-xl font-semibold text-amber-900 tabular-nums font-mono" data-testid="noble-cost-value">
                    {nobleCharge.toLocaleString()} 💎
                  </div>
                </div>
                {nobleCost <= 0 && (
                  <p className="text-xs text-red-600">This tier's price is not configured yet. Ask the administrator.</p>
                )}
              </>
            )}

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Note (optional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reference or comment" data-testid="recharge-note-input" />
            </div>

            <Button
              type="submit"
              className={opMode === "noble" ? "bg-amber-500 hover:bg-amber-600" : "bg-zinc-900 hover:bg-zinc-800"}
              disabled={loading || balance <= 0 || (opMode === "noble" && nobleCost <= 0)}
              data-testid="submit-recharge"
            >
              {opMode === "noble" ? <Crown className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {loading ? "Processing…" : opMode === "noble" ? "Activate Noble" : "Send Recharge"}
            </Button>
            {balance <= 0 && (
              <p className="text-xs text-red-600">You have no diamond balance. Request a refill from the administrator.</p>
            )}
          </form>
        </div>

        <div className="bg-zinc-950 text-white rounded-lg p-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-4">
            {reseller?.balances?.[platform]?.name || "Current"} Balance
          </div>
          <div className="flex items-baseline gap-2">
            <Diamond className="w-6 h-6 text-zinc-400" />
            <div className="font-display text-4xl font-semibold tabular-nums font-mono" data-testid="platform-balance">
              {balance.toLocaleString()}
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-zinc-800 text-xs text-zinc-400 space-y-1">
            <div className="flex justify-between"><span>Allocated</span><span className="font-mono">{(reseller?.balances?.[platform]?.allocated ?? 0).toLocaleString()}</span></div>
            <div className="flex justify-between"><span>Used</span><span className="font-mono">{(reseller?.balances?.[platform]?.used ?? 0).toLocaleString()}</span></div>
          </div>
          {opMode === "noble" && (
            <div className="mt-6 pt-6 border-t border-zinc-800 text-xs text-zinc-400 space-y-1" data-testid="noble-after-balance">
              <div className="flex justify-between"><span>Noble cost</span><span className="font-mono text-amber-400">{nobleCharge.toLocaleString()}</span></div>
              <div className="flex justify-between"><span>After purchase</span><span className="font-mono">{Math.max(0, balance - nobleCharge).toLocaleString()}</span></div>
            </div>
          )}
        </div>
      </div>

      {last && (
        <div className={`border rounded-lg p-6 ${last.status === "success" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`} data-testid="recharge-result">
          <div className="flex items-start gap-3">
            {last.status === "success" ? (
              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <div className={`font-display text-lg font-medium ${last.status === "success" ? "text-green-900" : "text-red-900"}`}>
                {last.type === "noble"
                  ? (last.status === "success" ? `${last.tier_label} ${last.mode === "renew" ? "renewed" : "activated"}` : "Noble recharge failed")
                  : (last.status === "success" ? "Recharge complete" : "Recharge failed")}
              </div>
              <div className="mt-1 text-sm text-zinc-700">
                {last.type === "noble" && <span className="inline-block mr-2 text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">NOBLE · {last.tier_label}</span>}
                {last.transferred && last.transferred !== last.diamonds ? (
                  <><span className="font-mono">{last.transferred.toLocaleString()}</span> received ({last.diamonds.toLocaleString()} deducted) → </>
                ) : (
                  <><span className="font-mono">{last.diamonds}</span> diamonds → </>
                )}
                {last.recipient?.nick ? <span className="font-medium">{last.recipient.nick} </span> : null}<span className="font-mono">{last.recipient_uid}</span>
              </div>
              <div className="mt-1 text-xs text-zinc-500 font-mono">{new Date(last.created_at).toLocaleString()}</div>
              {last.status === "failed" && last.error && (
                <div className="mt-2 text-sm text-red-700" data-testid="recharge-error">{last.error} — diamonds were refunded.</div>
              )}
              {last.test_mode && (
                <div className="mt-2 inline-block text-xs px-2 py-0.5 rounded bg-zinc-200 text-zinc-700 font-mono">
                  TEST MODE — upstream not called
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
